import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useToast } from '../../notifications';
import {
  getWhatsAppAppGroup,
  getWhatsAppAppGroupInvite,
  leaveWhatsAppAppGroup,
  renameWhatsAppAppGroup,
  revokeWhatsAppAppGroupInvite,
  sendWhatsAppAppGroupInvite,
  updateWhatsAppAppGroupParticipants,
  WhatsAppAppGroup,
  WhatsAppAppGroupInvite,
  WhatsAppAppGroupMember,
} from '../services/whatsAppAppGroupService';
import detailsStyles from './ConversationDetails.module.css';

interface WhatsAppAppGroupPanelProps {
  conversationId: string;
  title?: string;
  pictureUrl?: string;
  onTitleChanged?: (title: string) => void;
  onLeft?: () => void;
}

const WhatsAppAppGroupPanel: React.FC<WhatsAppAppGroupPanelProps> = ({
  conversationId,
  title,
  pictureUrl,
  onTitleChanged,
  onLeft,
}) => {
  const { t } = useTranslation('conversations');
  const { showToast } = useToast();
  const [group, setGroup] = useState<WhatsAppAppGroup | null>(null);
  const [invite, setInvite] = useState<WhatsAppAppGroupInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [subject, setSubject] = useState(title ?? '');
  const [memberNumber, setMemberNumber] = useState('');
  const [inviteNumber, setInviteNumber] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextGroup, nextInvite] = await Promise.all([
        getWhatsAppAppGroup(conversationId),
        getWhatsAppAppGroupInvite(conversationId).catch(() => null),
      ]);
      setGroup(nextGroup);
      setInvite(nextInvite);
      setSubject(nextGroup.title ?? title ?? '');
    } catch {
      showToast(t('failedToLoadWhatsAppGroup', { defaultValue: 'Failed to load WhatsApp group.' }), 'error');
    } finally {
      setLoading(false);
    }
  }, [conversationId, showToast, t, title]);

  useEffect(() => {
    setExpanded(false);
    setMemberNumber('');
    setInviteNumber('');
  }, [conversationId]);

  useEffect(() => {
    void load();
  }, [load]);

  const run = useCallback(async (work: () => Promise<void>, successKey: string, fallback: string) => {
    if (busy) {
      return;
    }

    setBusy(true);
    try {
      await work();
      showToast(t(successKey, { defaultValue: fallback }), 'success');
      await load();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('whatsAppGroupActionFailed', { defaultValue: 'WhatsApp group action failed.' }), 'error');
    } finally {
      setBusy(false);
    }
  }, [busy, load, showToast, t]);

  const displayTitle = group?.title || title || t('whatsAppGroup', { defaultValue: 'WhatsApp group' });
  const memberCount = group?.members.length ?? 0;
  const avatarUrl = pictureUrl || group?.pictureUrl;
  const inviteValue = invite?.inviteUrl || invite?.inviteCode || '';
  const subjectDirty = subject.trim().length > 0 && subject.trim() !== (group?.title ?? title ?? '').trim();

  const toggleLabel = expanded
    ? t('collapseWhatsAppGroup', { defaultValue: 'Hide group settings' })
    : t('expandWhatsAppGroup', { defaultValue: 'Show group settings' });

  return (
    <section className={detailsStyles.groupPanel} aria-label={t('whatsAppGroup', { defaultValue: 'WhatsApp group' })}>
      <button
        type="button"
        className={detailsStyles.groupPanelToggle}
        aria-expanded={expanded}
        aria-controls={`whatsapp-group-panel-${conversationId}`}
        onClick={() => setExpanded(current => !current)}
      >
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className={detailsStyles.groupPanelPicture} />
        ) : (
          <span className={detailsStyles.groupPanelMark} aria-hidden="true">
            {renderWhatsAppIcon()}
          </span>
        )}
        <span className={detailsStyles.groupPanelHeading}>
          <span className={detailsStyles.groupPanelTitle}>{displayTitle}</span>
          <span className={detailsStyles.groupPanelMeta}>
            {loading && !group
              ? t('loading', { ns: 'common', defaultValue: 'Loading…' })
              : t('whatsAppGroupMembersCount', { count: memberCount, defaultValue: '{{count}} members' })}
          </span>
        </span>
        <span className={detailsStyles.groupPanelToggleMeta}>
          <span className={detailsStyles.groupPanelToggleHint}>{toggleLabel}</span>
          <span className={`${detailsStyles.groupPanelChevron} ${expanded ? detailsStyles.groupPanelChevronOpen : ''}`} aria-hidden="true">
            ▾
          </span>
        </span>
      </button>

      {expanded ? (
        <div className={detailsStyles.groupPanelBody} id={`whatsapp-group-panel-${conversationId}`}>
          {loading && !group ? (
            <div className={detailsStyles.groupPanelSkeleton} />
          ) : (
            <>
              <div className={detailsStyles.groupPanelCard}>
                <div className={detailsStyles.groupPanelCardLabel}>
                  {t('whatsAppGroupName', { defaultValue: 'Group name' })}
                </div>
                <div className={detailsStyles.groupPanelRow}>
                  <input
                    className={detailsStyles.groupPanelInput}
                    value={subject}
                    onChange={event => setSubject(event.target.value)}
                    placeholder={t('whatsAppGroupName', { defaultValue: 'Group name' })}
                    aria-label={t('whatsAppGroupName', { defaultValue: 'Group name' })}
                  />
                  <button
                    type="button"
                    className={detailsStyles.groupPanelButton}
                    disabled={busy || !subjectDirty}
                    onClick={() => void run(async () => {
                      await renameWhatsAppAppGroup(conversationId, subject.trim());
                      onTitleChanged?.(subject.trim());
                    }, 'whatsAppGroupRenamed', 'Group renamed')}
                  >
                    {t('rename', { defaultValue: 'Rename' })}
                  </button>
                </div>
              </div>

              <div className={detailsStyles.groupPanelCard}>
                <div className={detailsStyles.groupPanelCardLabel}>
                  {t('whatsAppGroupMembers', { defaultValue: 'Members' })}
                  <span className={detailsStyles.groupPanelCount}>{memberCount}</span>
                </div>
                <div className={detailsStyles.groupMembers}>
                  {(group?.members ?? []).map(member => (
                    <GroupMemberRow
                      key={member.jid}
                      member={member}
                      busy={busy}
                      onPromoteOrDemote={() => void run(
                        () => updateWhatsAppAppGroupParticipants(
                          conversationId,
                          isWhatsAppGroupAdmin(member.admin) ? 'demote' : 'promote',
                          [member.phoneNumber || member.jid],
                        ),
                        isWhatsAppGroupAdmin(member.admin) ? 'whatsAppGroupMemberDemoted' : 'whatsAppGroupMemberPromoted',
                        isWhatsAppGroupAdmin(member.admin) ? 'Admin removed' : 'Member promoted',
                      )}
                      onRemove={() => void run(
                        () => updateWhatsAppAppGroupParticipants(conversationId, 'remove', [member.phoneNumber || member.jid]),
                        'whatsAppGroupMemberRemoved',
                        'Member removed',
                      )}
                    />
                  ))}
                </div>
                <div className={detailsStyles.groupPanelRow}>
                  <input
                    className={detailsStyles.groupPanelInput}
                    value={memberNumber}
                    onChange={event => setMemberNumber(event.target.value)}
                    placeholder={t('whatsAppGroupAddPlaceholder', { defaultValue: 'Add a phone number' })}
                    inputMode="tel"
                    aria-label={t('whatsAppGroupAddPlaceholder', { defaultValue: 'Add a phone number' })}
                  />
                  <button
                    type="button"
                    className={detailsStyles.groupPanelButton}
                    disabled={busy || !memberNumber.trim()}
                    onClick={() => void run(async () => {
                      await updateWhatsAppAppGroupParticipants(conversationId, 'add', [memberNumber.trim()]);
                      setMemberNumber('');
                    }, 'whatsAppGroupMemberAdded', 'Member added')}
                  >
                    {t('add', { ns: 'common', defaultValue: 'Add' })}
                  </button>
                </div>
              </div>

              <div className={detailsStyles.groupPanelCard}>
                <div className={detailsStyles.groupPanelCardLabel}>
                  {t('whatsAppGroupInvite', { defaultValue: 'Invite link' })}
                </div>
                <p className={detailsStyles.groupPanelCardHint}>
                  {inviteValue
                    ? t('whatsAppGroupInviteHint', { defaultValue: 'Share this link so people can join the group.' })
                    : t('whatsAppGroupInviteUnavailable', { defaultValue: 'No invite link yet' })}
                </p>
                {inviteValue ? (
                  <div className={detailsStyles.groupInviteLink}>
                    <code className={detailsStyles.groupInviteValue} title={inviteValue}>{inviteValue}</code>
                    <button
                      type="button"
                      className={detailsStyles.groupPanelGhostButton}
                      disabled={busy}
                      onClick={() => {
                        void navigator.clipboard.writeText(inviteValue);
                        showToast(t('copied', { defaultValue: 'Copied' }), 'success');
                      }}
                    >
                      {t('copyInvite', { defaultValue: 'Copy' })}
                    </button>
                    <button
                      type="button"
                      className={detailsStyles.groupPanelGhostButton}
                      disabled={busy}
                      onClick={() => void run(() => revokeWhatsAppAppGroupInvite(conversationId), 'whatsAppGroupInviteRevoked', 'Invite revoked')}
                    >
                      {t('revokeInvite', { defaultValue: 'Revoke' })}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={detailsStyles.groupPanelGhostButton}
                    disabled={busy}
                    onClick={() => void load()}
                  >
                    {t('refreshWhatsAppGroupInvite', { defaultValue: 'Refresh invite' })}
                  </button>
                )}
                <div className={detailsStyles.groupPanelRow}>
                  <input
                    className={detailsStyles.groupPanelInput}
                    value={inviteNumber}
                    onChange={event => setInviteNumber(event.target.value)}
                    placeholder={t('whatsAppGroupInvitePlaceholder', { defaultValue: 'Send invite to a phone number' })}
                    inputMode="tel"
                    aria-label={t('whatsAppGroupInvitePlaceholder', { defaultValue: 'Send invite to a phone number' })}
                  />
                  <button
                    type="button"
                    className={detailsStyles.groupPanelButton}
                    disabled={busy || !inviteNumber.trim()}
                    onClick={() => void run(async () => {
                      await sendWhatsAppAppGroupInvite(conversationId, [inviteNumber.trim()]);
                      setInviteNumber('');
                    }, 'whatsAppGroupInviteSent', 'Invite sent')}
                  >
                    {t('sendInvite', { defaultValue: 'Send' })}
                  </button>
                </div>
              </div>

              <button
                type="button"
                className={detailsStyles.groupPanelDangerButton}
                disabled={busy}
                onClick={() => {
                  if (!window.confirm(t('leaveWhatsAppGroupConfirm', { defaultValue: 'Leave this WhatsApp group?' }))) {
                    return;
                  }

                  void run(async () => {
                    await leaveWhatsAppAppGroup(conversationId);
                    onLeft?.();
                  }, 'leftWhatsAppGroup', 'Left group');
                }}
              >
                {t('leaveWhatsAppGroup', { defaultValue: 'Leave group' })}
              </button>
            </>
          )}
        </div>
      ) : null}
    </section>
  );
};

const GroupMemberRow: React.FC<{
  member: WhatsAppAppGroupMember;
  busy: boolean;
  onPromoteOrDemote: () => void;
  onRemove: () => void;
}> = ({ member, busy, onPromoteOrDemote, onRemove }) => {
  const { t } = useTranslation('conversations');
  const identity = useMemo(() => formatMemberIdentity(member), [member]);
  const role = memberRole(member.admin);
  const isAdmin = isWhatsAppGroupAdmin(member.admin);
  const roleLabel = role === 'superadmin'
    ? t('whatsAppGroupSuperAdmin', { defaultValue: 'Super admin' })
    : role === 'admin'
      ? t('whatsAppGroupAdmin', { defaultValue: 'Admin' })
      : null;
  const primary = identity.unknown
    ? t('whatsAppGroupUnknownMember', { defaultValue: 'WhatsApp user' })
    : identity.primary;

  return (
    <div className={detailsStyles.groupMemberRow}>
      <span className={detailsStyles.groupMemberAvatar} aria-hidden="true">{identity.initials}</span>
      <span className={detailsStyles.groupMemberCopy}>
        <span className={detailsStyles.groupMemberName}>{primary}</span>
        {identity.secondary ? <span className={detailsStyles.groupMemberSub}>{identity.secondary}</span> : null}
      </span>
      {roleLabel ? <span className={`${detailsStyles.groupMemberBadge} ${role === 'superadmin' ? detailsStyles.groupMemberBadgeSuper : ''}`}>{roleLabel}</span> : null}
      <span className={detailsStyles.groupMemberActions}>
        <button
          type="button"
          className={detailsStyles.groupMemberIconButton}
          disabled={busy}
          title={isAdmin
            ? t('demote', { defaultValue: 'Demote' })
            : t('promote', { defaultValue: 'Promote' })}
          aria-label={isAdmin
            ? t('demote', { defaultValue: 'Demote' })
            : t('promote', { defaultValue: 'Promote' })}
          onClick={onPromoteOrDemote}
        >
          {isAdmin ? renderDemoteIcon() : renderPromoteIcon()}
        </button>
        <button
          type="button"
          className={`${detailsStyles.groupMemberIconButton} ${detailsStyles.groupMemberIconDanger}`}
          disabled={busy}
          title={t('remove', { ns: 'common', defaultValue: 'Remove' })}
          aria-label={t('remove', { ns: 'common', defaultValue: 'Remove' })}
          onClick={onRemove}
        >
          {renderRemoveIcon()}
        </button>
      </span>
    </div>
  );
};

const formatMemberIdentity = (
  member: WhatsAppAppGroupMember,
): { primary: string; secondary?: string; initials: string; unknown?: boolean } => {
  const name = member.name?.trim();
  const phone = formatPhone(member.phoneNumber) || formatPhoneFromJid(member.jid);
  const technical = formatTechnicalId(member.jid);

  if (name) {
    return {
      primary: name,
      secondary: phone && phone !== name ? phone : undefined,
      initials: initialsFrom(name),
    };
  }

  if (phone) {
    return {
      primary: phone,
      secondary: technical && technical !== phone ? technical : undefined,
      initials: initialsFrom(phone),
    };
  }

  return {
    primary: 'WhatsApp user',
    secondary: technical,
    initials: 'WA',
    unknown: true,
  };
};

const formatPhone = (value?: string): string | undefined => {
  const raw = value?.trim();
  if (!raw) {
    return undefined;
  }

  if (raw.includes('@')) {
    return undefined;
  }

  const digits = raw.replace(/\D/g, '');
  if (digits.length < 7) {
    return undefined;
  }

  return raw.startsWith('+') ? raw : `+${digits}`;
};

const formatPhoneFromJid = (jid: string): string | undefined => {
  const local = jid.split('@')[0] ?? '';
  if (jid.endsWith('@s.whatsapp.net') || jid.endsWith('@c.us')) {
    return formatPhone(local);
  }

  return undefined;
};

const formatTechnicalId = (jid: string): string | undefined => {
  const local = jid.split('@')[0]?.trim();
  if (!local) {
    return undefined;
  }

  if (jid.endsWith('@lid')) {
    return local;
  }

  if (jid.includes('@')) {
    return undefined;
  }

  return jid;
};

const initialsFrom = (value: string): string => {
  const parts = value.replace(/^\+/, '').trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  const compact = parts[0] ?? 'WA';
  return compact.slice(0, 2).toUpperCase();
};

const memberRole = (admin?: string): 'superadmin' | 'admin' | 'member' => {
  const value = admin?.trim().toLowerCase();
  if (value === 'superadmin') {
    return 'superadmin';
  }

  return isWhatsAppGroupAdmin(admin) ? 'admin' : 'member';
};

const isWhatsAppGroupAdmin = (admin?: string): boolean => {
  const value = admin?.trim().toLowerCase();
  return Boolean(value) && value !== 'false' && value !== '0' && value !== 'member';
};

const renderWhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
    <path
      d="M12 3.6c-4.4 0-8 3.5-8 7.8 0 1.7.6 3.3 1.6 4.6L4.4 20.4l4.6-1.2c1.2.7 2.6 1 4 1 4.4 0 8-3.5 8-7.8S16.4 3.6 12 3.6Z"
      fill="currentColor"
      opacity="0.22"
    />
    <path
      d="M8.2 9.2c.2-.5.4-.5.6-.5h.5c.2 0 .4 0 .5.4.2.5.6 1.7.6 1.8.1.2 0 .3-.1.5l-.3.3c-.1.1-.2.3-.1.5.2.3.8 1.3 1.8 1.8 1 .6 1.4.6 1.6.5.2-.1.5-.3.6-.5.1-.2.3-.2.5-.1l1.4.7c.2.1.3.1.4.3 0 .2 0 1-.5 1.5-.4.5-1.2.8-2 .7-1.7-.1-3.6-1-4.9-2.7-1.2-1.6-1.6-3.1-1.7-3.6-.1-.8.2-1.5.6-1.9Z"
      fill="currentColor"
    />
  </svg>
);

const renderPromoteIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path d="M12 5.5 14.1 10l4.9.5-3.7 3.3.1 4.9L12 16.4 8.6 18.7l.1-4.9L5 10.5 9.9 10 12 5.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
  </svg>
);

const renderDemoteIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path d="M8 10.5 12 14.5 16 10.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 6.5v8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const renderRemoveIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" aria-hidden="true">
    <path d="M6 6l12 12M18 6 6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default WhatsAppAppGroupPanel;
